import React, { useState } from "react";
import {
  format,
  isToday,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfDay,
  differenceInCalendarDays,
} from "date-fns";
import DateRangePicker from "../DatePicker/DateRange";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from "@heroicons/react/24/outline";
import { VIEW_TYPES } from "./constants";
import { ViewType, IndustryType } from "./types/common";

interface SchedulerToolbarProps {
  currentView: ViewType;
  currentDate: Date;
  onViewChange: (view: ViewType) => void;
  onDateChange: (date: Date) => void;
  availableViews?: Array<{
    type: ViewType;
    label: string;
  }>;
  onZoomChange: (zoom: number) => void;
  zoom?: number;
  selectedDateRange?: {
    start: Date;
    end: Date;
  };
  onDateRangeChange?: (range: { start: Date; end: Date }) => void;
  useDateRange?: boolean;
  availableIndustryTypes?: IndustryType[];
  selectedIndustryTypes?: IndustryType[];
  onIndustryTypeChange?: (types: IndustryType[]) => void;
  showMapButton?: boolean;
  onMapToggle?: () => void;
  isMapVisible?: boolean;
  showSchedulerToggle?: boolean;
  onSchedulerToggle?: () => void;
  isSchedulerVisible?: boolean;
}

const SchedulerToolbar: React.FC<SchedulerToolbarProps> = ({
  currentView,
  currentDate,
  onViewChange,
  onDateChange,
  availableViews = [],
  onZoomChange,
  zoom = 1,
  selectedDateRange,
  onDateRangeChange,
  useDateRange = false,
  availableIndustryTypes = [
    "trucking",
    "hvac",
    "plumbing",
    "electrical",
    "general",
  ],
  selectedIndustryTypes = [],
  onIndustryTypeChange,
  showMapButton = false,
  onMapToggle,
  isMapVisible = false,
  showSchedulerToggle = false,
  onSchedulerToggle,
  isSchedulerVisible = true,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const isTimeline = currentView === VIEW_TYPES.TIMELINE;
  const isTimelineRangeMode = isTimeline && useDateRange;

  const clampToMaxTimelineRange = (start: Date, end: Date) => {
    // Dispatch Timeline should stay predictable + performant.
    // Enforce max 7 days (inclusive).
    const normalizedStart = startOfDay(start);
    const normalizedEnd = startOfDay(end);
    const days = differenceInCalendarDays(normalizedEnd, normalizedStart) + 1;
    if (days <= 7) return { start: normalizedStart, end: normalizedEnd };
    return { start: normalizedStart, end: addDays(normalizedStart, 6) };
  };

  const setDateRange = (start: Date, end: Date) => {
    const range = isTimelineRangeMode
      ? clampToMaxTimelineRange(start, end)
      : { start, end };
    onDateChange(range.start);
    onDateRangeChange?.(range);
    setIsDatePickerOpen(false);
  };

  const handlePrevious = () => {
    if (currentView === VIEW_TYPES.TIMELINE) {
      if (selectedDateRange && useDateRange) {
        // Move by the inclusive number of days in the current range
        const daysToMove =
          differenceInCalendarDays(
            startOfDay(selectedDateRange.end),
            startOfDay(selectedDateRange.start),
          ) + 1;

        // Move the entire range back by its duration
        const newStart = subDays(selectedDateRange.start, daysToMove);
        const newEnd = subDays(selectedDateRange.end, daysToMove);

        setDateRange(newStart, newEnd);
      } else {
        // Default to moving by 1 day if no range is set
        onDateChange(subDays(currentDate, 1));
      }
    } else {
      // Handle other views (keeping existing logic)
      const newDate = new Date(currentDate);
      switch (currentView) {
        case VIEW_TYPES.DAY:
          newDate.setDate(newDate.getDate() - 1);
          break;
        case VIEW_TYPES.WEEK:
          newDate.setDate(newDate.getDate() - 7);
          break;
        case VIEW_TYPES.MONTH:
          newDate.setMonth(newDate.getMonth() - 1);
          break;
        default:
          newDate.setDate(newDate.getDate() - 1);
      }
      onDateChange(newDate);
    }
  };

  const handleNext = () => {
    if (currentView === VIEW_TYPES.TIMELINE) {
      if (selectedDateRange && useDateRange) {
        // Move by the inclusive number of days in the current range
        const daysToMove =
          differenceInCalendarDays(
            startOfDay(selectedDateRange.end),
            startOfDay(selectedDateRange.start),
          ) + 1;

        // Move the entire range forward by its duration
        const newStart = addDays(selectedDateRange.start, daysToMove);
        const newEnd = addDays(selectedDateRange.end, daysToMove);

        setDateRange(newStart, newEnd);
      } else {
        // Default to moving by 1 day if no range is set
        onDateChange(addDays(currentDate, 1));
      }
    } else {
      // Handle other views (keeping existing logic)
      const newDate = new Date(currentDate);
      switch (currentView) {
        case VIEW_TYPES.DAY:
          newDate.setDate(newDate.getDate() + 1);
          break;
        case VIEW_TYPES.WEEK:
          newDate.setDate(newDate.getDate() + 7);
          break;
        case VIEW_TYPES.MONTH:
          newDate.setMonth(newDate.getMonth() + 1);
          break;
        default:
          newDate.setDate(newDate.getDate() + 1);
      }
      onDateChange(newDate);
    }
  };

  const handleZoomIn = () => {
    onZoomChange(Math.min(zoom + 0.2, 2));
  };

  const handleZoomOut = () => {
    onZoomChange(Math.max(zoom - 0.2, 0.5));
  };

  const handleDateChange = (update) => {
    if (useDateRange) {
      const [startDate, endDate] = update;
      if (startDate && endDate) {
        // Only update if we have both dates
        setDateRange(startDate, endDate);
      } else if (startDate) {
        // If we only have start date, update the current date
        onDateChange(startDate);
      }
    } else {
      // Single date mode
      const selectedDate = Array.isArray(update) ? update[0] : update;
      onDateChange(selectedDate);
    }

    setIsDatePickerOpen(false);
  };

  const renderedLabel = isToday(currentDate)
    ? `Today, ${format(currentDate, "MMM dd, yyyy")}`
    : format(currentDate, "MMM dd, yyyy");

  const rangeDayCount =
    useDateRange && selectedDateRange
      ? differenceInCalendarDays(
          startOfDay(selectedDateRange.end),
          startOfDay(selectedDateRange.start),
        ) + 1
      : null;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 bg-white p-2 border-b border-gray-200">
        {/* Left side: Date Navigation */}
        <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
          {/* Date Navigation */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={handlePrevious}
                className="p-1.5 hover:bg-gray-50 text-gray-600 border-r border-gray-200"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <DateRangePicker
                startDate={
                  useDateRange ? selectedDateRange?.start : currentDate
                }
                endDate={useDateRange ? selectedDateRange?.end : currentDate}
                className="border-0"
                calendarClassName="!flex flex-row"
                selectsRange={useDateRange}
                maxRangeDays={isTimelineRangeMode ? 7 : undefined}
                customInput={
                  <button
                    onClick={() => setIsDatePickerOpen(true)}
                    className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none text-left whitespace-nowrap"
                  >
                    <div className="flex flex-col leading-tight">
                      {/* Compact label below sm — drops the year — to keep the
                          toolbar single-row on tablet portrait. Full label with
                          year shown from sm: upward. */}
                      <span className="hidden sm:inline">
                        {useDateRange && selectedDateRange
                          ? `${format(selectedDateRange.start, "MMM dd")} - ${format(
                              selectedDateRange.end,
                              "MMM dd, yyyy",
                            )}`
                          : format(currentDate, "MMM dd, yyyy")}
                      </span>
                      <span className="sm:hidden">
                        {useDateRange && selectedDateRange
                          ? `${format(selectedDateRange.start, "MMM d")} – ${format(
                              selectedDateRange.end,
                              "MMM d",
                            )}`
                          : format(currentDate, "MMM d, yyyy")}
                      </span>
                      {isTimelineRangeMode && rangeDayCount !== null && (
                        <span className="text-[10px] sm:text-xs text-gray-500">
                          {`${rangeDayCount} ${
                            rangeDayCount === 1 ? "day" : "days"
                          } (max 7)`}
                        </span>
                      )}
                    </div>
                  </button>
                }
                onChange={handleDateChange}
                dateFormat="MMM dd, yyyy"
                popperClassName={null}
                popperModifiers={null}
              />
              <button
                onClick={handleNext}
                className="p-1.5 hover:bg-gray-50 text-gray-600 border-l border-gray-200"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Quick actions (Timeline / Dispatch) */}
            {isTimeline && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                  onClick={() => {
                    const today = startOfDay(new Date());
                    if (useDateRange) {
                      setDateRange(today, today);
                    } else {
                      onDateChange(today);
                    }
                  }}
                >
                  Today
                </button>
                {useDateRange && (
                  <>
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                      onClick={() => {
                        const start = startOfDay(currentDate);
                        setDateRange(start, addDays(start, 6));
                      }}
                    >
                      Next 7
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                      onClick={() => {
                        const start = startOfWeek(currentDate, {
                          weekStartsOn: 1,
                        });
                        const end = endOfWeek(currentDate, { weekStartsOn: 1 });
                        setDateRange(start, end);
                      }}
                    >
                      This week
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right side: View Controls and Zoom */}
        <div className="flex flex-wrap items-center gap-y-2 gap-x-3 sm:gap-x-4 ml-auto">
          {/* Zoom Controls */}
          {isTimeline && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                disabled={zoom <= 0.5}
                title="Zoom Out"
              >
                <MagnifyingGlassMinusIcon className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700 w-10 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                disabled={zoom >= 2}
                title="Zoom In"
              >
                <MagnifyingGlassPlusIcon className="w-5 h-5" />
              </button>
            </div>
          )}
          {/* View Selection — wraps below lg, stays compact on tablets so a
              5+ button row doesn't push the date nav off-screen. */}
          <div className="flex flex-wrap items-center gap-1.5 lg:gap-2">
            {availableViews.map((view) => (
              <button
                key={view.type}
                type="button"
                onClick={() => onViewChange(view.type as ViewType)}
                className={`px-2.5 lg:px-3 py-1.5 lg:min-w-[88px] rounded-lg border text-xs sm:text-sm font-medium transition-colors ${
                  currentView === view.type
                    ? "bg-primary-100 text-primary-800 border-primary-400 shadow-sm hover:bg-primary-100"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {view.label}
              </button>
            ))}
          </div>

          {/* Filters Toggle */}
          {/* <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded hover:bg-gray-100 ${
              showFilters ? "bg-gray-200 text-blue-600" : "text-gray-600"
            }`}
            title="Toggle Filters"
          >
            <FunnelIcon className="w-5 h-5" />
          </button> */}
        </div>
      </div>

      {/* Filter Section - Rendered below toolbar or as a panel */}
      {showFilters && (
        <div className="bg-gray-50 p-3 border-b border-gray-200 flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="flex items-center space-x-2">
            <label
              htmlFor="eventTypeFilter"
              className="text-sm font-medium text-gray-700"
            >
              Type:
            </label>
            <select
              id="eventTypeFilter"
              multiple // For now, a simple multi-select. Would replace with a better component.
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              value={selectedIndustryTypes}
              onChange={(e) => {
                const selectedOptions = Array.from(
                  e.target.selectedOptions,
                  (option) => option.value as IndustryType,
                );
                onIndustryTypeChange?.(selectedOptions);
              }}
            >
              {availableIndustryTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}{" "}
                  {/* Capitalize first letter */}
                </option>
              ))}
            </select>
          </div>
          {/* Placeholder for Status Filter */}
          {/* Placeholder for Resource Filter */}
        </div>
      )}
    </>
  );
};

export default SchedulerToolbar;
